import React, { useState, useMemo, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ProjectOutletContext } from '@/types/project';
import { ShoppingCart, Package, Receipt, Plus, Sparkles, ArrowRight, FilePlus, Minus, Trash2, FileText, MoreVertical, Check, AlertCircle, FileCheck, Building2, User, ClipboardList, FileSignature, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DndContext, DragOverlay, closestCenter, DragEndEvent, DragStartEvent, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSensors, useSensor, PointerSensor, TouchSensor, KeyboardSensor } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import DashboardCard from '@/components/ui/DashboardCard';
import DashboardButton from '@/components/ui/DashboardButton';
import GlassPill from '@/components/ui/GlassPill';
import SidebarLayout from '@/components/layout/SidebarLayout';
import { EmptyStateLayout } from '@/components/shared/empty-states/EmptyStateLayout';
import { ServicesCatalogSidebar } from '@/components/services/ServicesCatalogSidebar';
import { PurchasedServiceCard } from '@/components/services/PurchasedServiceCard';
import { CreateWorkOrderModal } from '@/components/services/CreateWorkOrderModal';
import { WorkOrderSpecificationsModal } from '@/components/services/WorkOrderSpecificationsModal';
import { ServiceAgreementModal } from '@/components/services/ServiceAgreementModal';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useServicesCatalog } from '@/hooks/services/use-services-catalog';
import { useShoppingCart } from '@/hooks/services/use-shopping-cart';
import { useProjectInvoices } from '@/hooks/services/use-project-invoices';
import { useServiceGeneration } from '@/hooks/services/use-service-generation';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/errors/use-toast';
import { useAuth } from '@/contexts/auth';

// Droppable Work Order Section Component
const DroppableWorkOrderSection = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; workOrderId?: string }
>(({ children, workOrderId }, forwardedRef) => {
  const { setNodeRef, isOver } = useDroppable({
    id: workOrderId ? `work-order-${workOrderId}` : 'work-orders-section',
    data: {
      type: 'work-order',
      workOrderId,
    },
  });

  // Combine the refs
  const combinedRef = React.useCallback(
    (element: HTMLDivElement | null) => {
      setNodeRef(element);
      if (typeof forwardedRef === 'function') {
        forwardedRef(element);
      } else if (forwardedRef) {
        forwardedRef.current = element;
      }
    },
    [setNodeRef, forwardedRef]
  );

  return (
    <div
      ref={combinedRef}
      className={cn(
        "transition-all duration-200",
        isOver && "ring-2 ring-purple-500 ring-opacity-50 bg-purple-50 dark:bg-purple-900/10 rounded-lg"
      )}
    >
      {children}
    </div>
  );
});

DroppableWorkOrderSection.displayName = 'DroppableWorkOrderSection';

const ProjectServicesPage: React.FC = () => {
  const { project } = useOutletContext<ProjectOutletContext>();
  const { isMobile } = useIsMobile();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // State for service creation flow
  const [currentQuestion, setCurrentQuestion] = useState<1 | 2>(1);
  const [problemStatement, setProblemStatement] = useState('');
  const [solutionDescription, setSolutionDescription] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSpecificationsModalOpen, setIsSpecificationsModalOpen] = useState(false);
  const [selectedWorkOrderForSpecs, setSelectedWorkOrderForSpecs] = useState<any>(null);
  const [workOrderSpecsStatus, setWorkOrderSpecsStatus] = useState<Record<string, boolean>>({});
  const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false);
  const [selectedWorkOrderForAgreement, setSelectedWorkOrderForAgreement] = useState<any>(null);
  const [workOrderAgreementStatus, setWorkOrderAgreementStatus] = useState<Record<string, boolean>>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeService, setActiveService] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState<Record<string, boolean>>({});
  
  // State for company info
  const [companyInfo, setCompanyInfo] = useState<any>(null);
  const [isLoadingCompanyInfo, setIsLoadingCompanyInfo] = useState(true);
  
  // State for personal info
  const [personalInfo, setPersonalInfo] = useState<any>(null);
  const [isLoadingPersonalInfo, setIsLoadingPersonalInfo] = useState(true);
  
  // Hooks
  const { user } = useAuth();
  const { data: services = [], isLoading: isLoadingServices } = useServicesCatalog();
  const { data: invoices = [], isLoading: isLoadingInvoices } = useProjectInvoices(project?.id);
  const {
    workOrders,
    workOrder,
    cartItems,
    cartTotals,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    isAddingToCart,
    isUpdatingCart,
    isRemovingFromCart,
  } = useShoppingCart(project?.id);
  
  const { generateService, isGenerating: isGeneratingService } = useServiceGeneration();
  
  // Check specifications and agreement status for all work orders
  const checkWorkOrderStatuses = async () => {
    if (!workOrders || workOrders.length === 0) return;
    
    const specsStatusMap: Record<string, boolean> = {};
    const agreementStatusMap: Record<string, boolean> = {};
    
    for (const wo of workOrders) {
      try {
        const { data, error } = await supabase
          .from('workbook_services')
          .select('specifications, agreement_signed_by')
          .eq('id', wo.id)
          .single();
        
        if (!error && data) {
          // Check if specifications are complete (has problem statement at minimum)
          specsStatusMap[wo.id] = !!data.specifications?.problem_statement?.trim();
          // Check if agreement is signed
          agreementStatusMap[wo.id] = !!data.agreement_signed_by;
        } else {
          specsStatusMap[wo.id] = false;
          agreementStatusMap[wo.id] = false;
        }
      } catch (error) {
        console.error('Error checking work order status:', error);
        specsStatusMap[wo.id] = false;
        agreementStatusMap[wo.id] = false;
      }
    }
    
    setWorkOrderSpecsStatus(specsStatusMap);
    setWorkOrderAgreementStatus(agreementStatusMap);
  };
  
  // Handle work order submission
  const handleSubmitWorkOrder = async (workOrderId: string) => {
    setIsSubmitting(prev => ({ ...prev, [workOrderId]: true }));
    
    try {
      const { error } = await supabase
        .from('workbook_services')
        .update({ work_order_status: 'submitted' })
        .eq('id', workOrderId);
      
      if (error) throw error;
      
      // Send email notification to team
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          const response = await supabase.functions.invoke('send-work-order-email', {
            body: {
              workOrderId: workOrderId,
              projectId: project?.id,
            },
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          });
          
          if (response.error) {
            console.error('Failed to send email notification:', response.error);
            // Don't throw - email failure shouldn't block the submission
          } else {
            console.log('Email notification sent successfully');
          }
        }
      } catch (emailError) {
        console.error('Error sending email notification:', emailError);
        // Don't throw - email failure shouldn't block the submission
      }
      
      toast({
        title: 'Work Order Submitted',
        description: 'Your work order has been submitted successfully. Our team has been notified.',
        variant: 'default',
      });
      
      // Refetch the shopping cart data to update the UI
      // This will automatically update the workOrders through the useShoppingCart hook
      await queryClient.invalidateQueries({ queryKey: ['shopping-cart', project?.id] });
      
      // Also refetch invoices since submitted orders may appear there
      await queryClient.invalidateQueries({ queryKey: ['project-invoices', project?.id] });
      
      // Refresh the work order statuses
      await checkWorkOrderStatuses();
    } catch (error) {
      console.error('Error submitting work order:', error);
      toast({
        title: 'Submission Failed',
        description: 'Failed to submit work order. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(prev => ({ ...prev, [workOrderId]: false }));
    }
  };
  
  // Check statuses when work orders change
  React.useEffect(() => {
    checkWorkOrderStatuses();
  }, [workOrders]);
  
  // Check company information
  useEffect(() => {
    const loadCompanyInfo = async () => {
      if (!project?.id) return;
      
      setIsLoadingCompanyInfo(true);
      try {
        const { data, error } = await supabase
          .from('workbook')
          .select('company_info')
          .eq('id', project.id)
          .single();
        
        if (!error && data) {
          setCompanyInfo(data.company_info);
        }
      } catch (error) {
        console.error('Error loading company info:', error);
      } finally {
        setIsLoadingCompanyInfo(false);
      }
    };
    
    loadCompanyInfo();
  }, [project?.id]);
  
  // Check personal information
  useEffect(() => {
    const loadPersonalInfo = async () => {
      if (!user?.id) return;
      
      setIsLoadingPersonalInfo(true);
      try {
        const { data, error } = await supabase
          .from('users')
          .select('first_name, last_name')
          .eq('id', user.id)
          .single();
        
        if (!error && data) {
          setPersonalInfo(data);
        }
      } catch (error) {
        console.error('Error loading personal info:', error);
      } finally {
        setIsLoadingPersonalInfo(false);
      }
    };
    
    loadPersonalInfo();
  }, [user?.id]);
  
  // Check if personal info is complete
  const isPersonalInfoComplete = useMemo(() => {
    if (!personalInfo) return false;
    
    return !!(
      personalInfo.first_name?.trim() &&
      personalInfo.last_name?.trim()
    );
  }, [personalInfo]);
  
  // Check if company info is complete (updated to include all required fields)
  const isCompanyInfoComplete = useMemo(() => {
    if (!companyInfo) return false;
    
    return !!(
      companyInfo.company_name?.trim() &&
      companyInfo.abn?.trim() &&
      ((typeof companyInfo.registered_address === 'string' && companyInfo.registered_address?.trim()) ||
       (typeof companyInfo.registered_address === 'object' && companyInfo.registered_address?.street?.trim())) &&
      companyInfo.contact_email?.trim() &&
      companyInfo.contact_phone?.trim() &&
      companyInfo.website?.trim()
    );
  }, [companyInfo]);

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
    
    // Get service data from the dragged item
    if (active.data.current?.type === 'service') {
      setActiveService(active.data.current.service);
    }
  };

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveId(null);
    setActiveService(null);
    
    if (!over) return;
    
    // Check if we're dropping a service onto a work order
    if (active.data.current?.type === 'service' && over.data.current?.type === 'work-order') {
      const service = active.data.current.service;
      const workOrderId = over.data.current.workOrderId;
      
      try {
        if (workOrderId) {
          // Add to specific work order
          await addToCart({ 
            serviceId: service.id, 
            quantity: 1,
            workOrderId: workOrderId 
          });
          
          const workOrder = workOrders?.find(wo => wo.id === workOrderId);
          toast({
            title: 'Service Added', 
            description: `${service.name} has been added to ${workOrder?.service_name || 'the work order'}`,
            variant: 'default',
          });
        } else {
          // Add to first draft work order or create new one
          await addToCart({ 
            serviceId: service.id, 
            quantity: 1 
          });
          
          toast({
            title: 'Service Added',
            description: `${service.name} has been added to the work order`,
            variant: 'default',
          });
        }
      } catch (error) {
        console.error('Failed to add service:', error);
        toast({
          title: 'Error',
          description: 'Failed to add service to work order',
          variant: 'destructive',
        });
      }
    }
  };

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-300">
          Project not found.
        </p>
      </div>
    );
  }

  // Handle service creation flow
  const handleNextQuestion = () => {
    if (currentQuestion === 1 && problemStatement.trim()) {
      setCurrentQuestion(2);
    }
  };

  const handleSubmitService = async () => {
    if (!problemStatement.trim() || !solutionDescription.trim()) {
      toast({
        title: 'Missing Information',
        description: 'Please answer both questions to create a service.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await generateService(
        {
          problemStatement,
          solutionDescription,
        },
        project.id
      );
      
      // Reset form
      setProblemStatement('');
      setSolutionDescription('');
      setCurrentQuestion(1);
    } catch (error) {
      // Error is handled in the hook
    }
  };

  // Handle invoice actions
  const handleViewInvoice = (url: string) => {
    window.open(url, '_blank');
  };

  const handleDownloadInvoice = (url: string) => {
    window.open(url, '_blank');
  };

  // Empty state - no invoices and no work orders
  const shouldShowEmptyState = !isLoadingInvoices && invoices.length === 0 && (!workOrders || workOrders.length === 0);

  // Get service IDs for sortable context
  const serviceIds = services.map(s => `service-${s.id}`);

  // Mobile catalog sheet
  const CatalogSheet = () => (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          className={cn(
            "fixed bottom-4 right-4 z-50",
            "bg-gradient-to-r from-purple-600 to-blue-600",
            "hover:from-purple-700 hover:to-blue-700",
            "text-white shadow-lg",
            "h-14 px-4"
          )}
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Services
          {cartTotals.itemCount > 0 && (
            <Badge variant="secondary" className="ml-2">
              {cartTotals.itemCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md p-0">
        <div className="h-full flex flex-col">
          <SheetHeader className="px-4 py-3 border-b">
            <SheetTitle>Services Catalog</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-hidden">
            <ServicesCatalogSidebar
              services={services}
              cartItems={cartItems}
              cartTotals={cartTotals}
              onAddToCart={(serviceId, quantity) => addToCart({ serviceId, quantity })}
              onUpdateQuantity={(itemId, quantity) => 
                updateCartItem({ itemId, quantity })
              }
              onRemoveItem={removeFromCart}
              onClearCart={clearCart}
              isLoadingServices={isLoadingServices}
              isAddingToCart={isAddingToCart}
              isUpdating={isUpdatingCart}
              isRemoving={isRemovingFromCart}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={serviceIds} strategy={verticalListSortingStrategy}>
        <>
          {shouldShowEmptyState ? (
        /* Empty State with Service Creation */
        <EmptyStateLayout
          icon={<ShoppingCart className="h-8 w-8 text-purple-500 dark:text-purple-400" />}
          title="Work Order"
          description="Create a work order for custom solutions that deliver real results, fast"
          variant="purple"
        >
          <div className="max-w-2xl mx-auto w-full px-4">
            {/* Service Creation Flow - Always Visible */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="py-4 space-y-6"
            >
              {/* Progress Indicator */}
              <div className="flex items-center gap-2">
                <div className={cn(
                  "flex-1 h-2 rounded-full transition-colors",
                  currentQuestion >= 1 ? "bg-purple-600" : "bg-gray-200 dark:bg-gray-700"
                )} />
                <div className={cn(
                  "flex-1 h-2 rounded-full transition-colors",
                  currentQuestion >= 2 ? "bg-purple-600" : "bg-gray-200 dark:bg-gray-700"
                )} />
              </div>

              {/* Question 1 */}
              {currentQuestion === 1 && (
                <motion.div
                  key="question1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div>
                    <Label className="text-base font-medium mb-2 block text-left">
                      What problem are you trying to solve?
                    </Label>
                    <Textarea
                      value={problemStatement}
                      onChange={(e) => setProblemStatement(e.target.value)}
                      placeholder="Describe the challenge or pain point you're facing..."
                      className="min-h-[100px] w-full"
                      autoFocus
                    />
                  </div>
                  <Button
                    onClick={handleNextQuestion}
                    disabled={!problemStatement.trim()}
                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                  >
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </motion.div>
              )}

              {/* Question 2 */}
              {currentQuestion === 2 && (
                <motion.div
                  key="question2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div>
                    <Label className="text-base font-medium mb-2 block text-left">
                      What's your ideal solution look like?
                    </Label>
                    <Textarea
                      value={solutionDescription}
                      onChange={(e) => setSolutionDescription(e.target.value)}
                      placeholder="Describe what success looks like for you..."
                      className="min-h-[100px] w-full"
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentQuestion(1)}
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleSubmitService}
                      disabled={!solutionDescription.trim() || isGeneratingService}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                    >
                      {isGeneratingService ? (
                        <>
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="mr-2"
                          >
                            <Sparkles className="h-4 w-4" />
                          </motion.div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Create Work Order
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </EmptyStateLayout>
      ) : (
        /* Main Layout with Services */
        <SidebarLayout 
          sidebar={
            <ServicesCatalogSidebar
              services={services}
              cartItems={cartItems}
              cartTotals={cartTotals}
              onAddToCart={(serviceId, quantity) => addToCart({ serviceId, quantity })}
              onUpdateQuantity={(itemId, quantity) => 
                updateCartItem({ itemId, quantity })
              }
              onRemoveItem={removeFromCart}
              onClearCart={clearCart}
              isLoadingServices={isLoadingServices}
              isAddingToCart={isAddingToCart}
              isUpdating={isUpdatingCart}
              isRemoving={isRemovingFromCart}
            />
          }
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.1 }}
            className="space-y-6"
          >
            {/* Draft Work Orders Section - Make it droppable */}
            <DroppableWorkOrderSection>
              <DashboardCard className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Package className="text-blue-500 dark:text-blue-400" size={18} />
                    <h3 className="font-medium text-base">Draft Work Orders</h3>
                  {workOrders && workOrders.length > 0 && (
                    <GlassPill 
                      type={workOrders.length.toString()} 
                      variant="blue" 
                      className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
                    />
                  )}
                </div>
                
                
                {/* Create Work Order Button - Desktop */}
                <div className="hidden sm:block">
                  <DashboardButton
                    onClick={() => setIsCreateModalOpen(true)}
                    icon={FilePlus}
                    size="sm"
                    className="h-8 px-3 py-1.5 text-sm"
                  >
                    Create Work Order
                  </DashboardButton>
                </div>
              </div>
              
              {/* Create Work Order Button - Mobile (full width below header) */}
              <div className="sm:hidden mb-4">
                <DashboardButton
                  onClick={() => setIsCreateModalOpen(true)}
                  icon={FilePlus}
                  size="sm"
                  className="h-8 px-3 py-1.5 text-sm w-full"
                >
                  Create Work Order
                </DashboardButton>
              </div>

              {!workOrders || workOrders.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                  <Package className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    No draft work orders
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                    Drag services here or create custom services to start
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Work Order Items */}
                  <div className="grid gap-3">
                    <AnimatePresence mode="popLayout">
                      {workOrders && workOrders.map((wo, index) => {
                        const isSubmitted = wo.work_order_status === 'submitted';
                        return (
                        <DroppableWorkOrderSection key={wo.id} workOrderId={isSubmitted ? undefined : wo.id}>
                          <div className={cn(
                            "space-y-3",
                            "p-4 rounded-xl",
                            "bg-gradient-to-br from-purple-50/50 to-blue-50/50",
                            "dark:from-purple-900/10 dark:to-blue-900/10",
                            "border border-purple-200/50 dark:border-purple-700/30",
                            index > 0 && "mt-6"
                          )}>
                            {/* Work Order Header */}
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-semibold text-lg text-purple-900 dark:text-purple-100">
                                {wo.service_name}
                              </h4>
                              <GlassPill 
                                type={(wo.work_order_status || 'draft').charAt(0).toUpperCase() + (wo.work_order_status || 'draft').slice(1)} 
                                variant={wo.work_order_status === 'submitted' ? 'green' : 'purple'} 
                                className="text-xs"
                              />
                            </div>
                          
                          {/* Work Order Items */}
                          {wo.items && wo.items.length === 0 && !isSubmitted ? (
                            <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 border-dashed">
                              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                                No items added yet. Drag services from the catalog to build your work order.
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {wo.items?.map((item) => (
                                <motion.div
                                  key={item.id}
                                  layout
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: 100 }}
                                  className="group relative p-3 rounded-lg bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 hover:border-purple-300 dark:hover:border-purple-600 transition-all hover:shadow-md"
                                >
                                  <div className="flex items-center gap-4">
                                    {/* Service Info - Left */}
                                    <div className="flex-1 min-w-0">
                                      <h5 className="font-medium text-sm text-gray-700 dark:text-gray-300 truncate">
                                        {item.item_name}
                                      </h5>
                                      {item.item_description && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                                          {item.item_description}
                                        </p>
                                      )}
                                      {item.custom_requirements && (
                                        <div className="flex items-center gap-1 mt-1">
                                          <FileText className="h-3 w-3 text-gray-400" />
                                          <p className="text-xs text-gray-500 dark:text-gray-400 italic truncate">
                                            {item.custom_requirements}
                                          </p>
                                        </div>
                                      )}
                                      {item.item_category && (
                                        <div className="mt-1.5">
                                          <GlassPill 
                                            type={
                                              item.item_category === 'web_design' ? 'Web Design' :
                                              item.item_category === 'ai_automation' ? 'AI Automation' :
                                              item.item_category === 'ai_consulting' ? 'AI Consulting' :
                                              item.item_category
                                            }
                                            variant={
                                              item.item_category === 'web_design' ? 'orange' :
                                              item.item_category === 'ai_automation' ? 'green' :
                                              item.item_category === 'ai_consulting' ? 'purple' :
                                              'gray'
                                            }
                                            className="text-xs inline-block"
                                          />
                                        </div>
                                      )}
                                    </div>

                                    {/* Quantity Controls - Center */}
                                    {!isSubmitted ? (
                                    <div className="flex items-center gap-2">
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 rounded-full hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400 transition-colors"
                                        onClick={() => {
                                          if (item.quantity > 1) {
                                            updateCartItem({ 
                                              itemId: item.id, 
                                              quantity: item.quantity - 1 
                                            });
                                          }
                                        }}
                                        disabled={item.quantity <= 1 || isUpdatingCart}
                                      >
                                        <Minus className="h-3.5 w-3.5" />
                                      </Button>
                                      
                                      <div className="w-8 text-center">
                                        <span className="text-sm font-medium">{item.quantity}</span>
                                      </div>
                                      
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 rounded-full hover:bg-green-50 hover:text-green-600 dark:hover:bg-green-950 dark:hover:text-green-400 transition-colors"
                                        onClick={() => {
                                          updateCartItem({ 
                                            itemId: item.id, 
                                            quantity: item.quantity + 1 
                                          });
                                        }}
                                        disabled={isUpdatingCart}
                                      >
                                        <Plus className="h-3.5 w-3.5" />
                                      </Button>
                                    </div>
                                    ) : (
                                      <div className="text-center px-3">
                                        <span className="text-sm font-medium text-gray-500">Qty: {item.quantity}</span>
                                      </div>
                                    )}

                                    {/* Price - Right */}
                                    <div className="text-right">
                                      <motion.p 
                                        key={item.unit_price * item.quantity}
                                        initial={{ scale: 1.1 }}
                                        animate={{ scale: 1 }}
                                        className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                                      >
                                        ${(item.unit_price * item.quantity).toFixed(2)}
                                      </motion.p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        ${item.unit_price.toFixed(2)} each
                                      </p>
                                    </div>

                                    {/* Delete Button */}
                                    {!isSubmitted && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      className="h-7 w-7 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950 dark:hover:text-red-400 transition-all"
                                      onClick={() => removeFromCart(item.id)}
                                      disabled={isRemovingFromCart}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                    )}
                                  </div>
                                </motion.div>
                              ))}
                              
                              {/* Work Order Footer with Total and Generate Quote */}
                              <Separator className="my-3" />
                              <div className="flex items-center justify-between px-2">
                                <div>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Subtotal ({wo.items?.length || 0} items)
                                  </p>
                                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    ${
                                      (wo.items?.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0) || 0).toFixed(2)
                                    }
                                  </p>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                  {/* Personal Information Button */}
                                  {!isLoadingPersonalInfo && !isPersonalInfoComplete && (
                                    <Button
                                      className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white transition-all"
                                      onClick={() => {
                                        // Navigate to account page using React Router
                                        navigate('/account');
                                      }}
                                    >
                                      <User className="h-4 w-4 mr-2" />
                                      Personal Info
                                    </Button>
                                  )}
                                  
                                  {/* Company Information Button */}
                                  {!isLoadingCompanyInfo && !isCompanyInfoComplete && (
                                    <Button
                                      className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white transition-all"
                                      onClick={() => {
                                        // Navigate to settings and open company info accordion
                                        navigate(`/project/${project.id}/settings`, {
                                          state: { openCompanyInfo: true }
                                        });
                                      }}
                                    >
                                      <Building2 className="h-4 w-4 mr-2" />
                                      Company Info
                                    </Button>
                                  )}
                                  
                                  {/* Show Specifications and Agreement buttons if both personal and company info are complete */}
                                  {/* These should be visible for both draft and submitted orders */}
                                  {isPersonalInfoComplete && isCompanyInfoComplete && (
                                    <>
                                      <Button
                                        className={cn(
                                          "transition-all",
                                          workOrderSpecsStatus[wo.id]
                                            ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                                            : "bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
                                        )}
                                        onClick={() => {
                                          setSelectedWorkOrderForSpecs(wo);
                                          setIsSpecificationsModalOpen(true);
                                        }}
                                      >
                                        {workOrderSpecsStatus[wo.id] ? (
                                          <>
                                            <FileCheck className="h-4 w-4 mr-2" />
                                            Specifications
                                          </>
                                        ) : (
                                          <>
                                            <ClipboardList className="h-4 w-4 mr-2" />
                                            Specifications
                                          </>
                                        )}
                                      </Button>
                                      <Button
                                        className={cn(
                                          "transition-all",
                                          workOrderAgreementStatus[wo.id]
                                            ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                                            : "bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
                                        )}
                                        onClick={() => {
                                          setSelectedWorkOrderForAgreement(wo);
                                          setIsAgreementModalOpen(true);
                                        }}
                                      >
                                        {workOrderAgreementStatus[wo.id] ? (
                                          <>
                                            <FileCheck className="h-4 w-4 mr-2" />
                                            Agreement
                                          </>
                                        ) : (
                                          <>
                                            <FileSignature className="h-4 w-4 mr-2" />
                                            Agreement
                                          </>
                                        )}
                                      </Button>
                                      
                                      {/* Show Submit Request button ONLY for draft orders when both specs and agreement are complete */}
                                      {!isSubmitted && workOrderSpecsStatus[wo.id] && workOrderAgreementStatus[wo.id] && (
                                        <Button
                                          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white transition-all"
                                          onClick={() => handleSubmitWorkOrder(wo.id)}
                                          disabled={isSubmitting[wo.id]}
                                        >
                                          {isSubmitting[wo.id] ? (
                                            <>
                                              <Send className="h-4 w-4 mr-2 animate-pulse" />
                                              Submitting...
                                            </>
                                          ) : (
                                            <>
                                              <Send className="h-4 w-4 mr-2" />
                                              Submit Request
                                            </>
                                          )}
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                          </div>
                        </DroppableWorkOrderSection>
                        );
                      })}
                    </AnimatePresence>
                  </div>

                </div>
              )}
              </DashboardCard>
            </DroppableWorkOrderSection>

            {/* Active Work Orders Section */}
            <DashboardCard className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Receipt className="text-purple-500 dark:text-purple-400" size={18} />
                  <h3 className="font-medium text-base">Active Work Orders</h3>
                  {invoices.length > 0 && (
                    <GlassPill 
                      type={invoices.length.toString()} 
                      variant="purple" 
                      className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
                    />
                  )}
                </div>
              </div>

              {/* Invoices List */}
              {isLoadingInvoices ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-32 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse"
                    />
                  ))}
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-12">
                  <Receipt className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    No active work orders
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                    Active work orders will appear here once approved
                  </p>
                </div>
              ) : (
                <div className="grid gap-3">
                  <AnimatePresence mode="popLayout">
                    {invoices.map((invoice) => (
                      <PurchasedServiceCard
                        key={invoice.id}
                        invoice={invoice}
                        onViewInvoice={handleViewInvoice}
                        onDownloadInvoice={handleDownloadInvoice}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </DashboardCard>
          </motion.div>
        </SidebarLayout>
      )}

          {/* Mobile Catalog Button/Sheet */}
          {isMobile && !shouldShowEmptyState && <CatalogSheet />}
          
          {/* Create Work Order Modal */}
          <CreateWorkOrderModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            workbookId={project?.id || ''}
          />
          
          {/* Work Order Specifications Modal */}
          {selectedWorkOrderForSpecs && (
            <WorkOrderSpecificationsModal
              isOpen={isSpecificationsModalOpen}
              onClose={() => {
                setIsSpecificationsModalOpen(false);
                setSelectedWorkOrderForSpecs(null);
              }}
              workOrderId={selectedWorkOrderForSpecs.id}
              workOrderName={selectedWorkOrderForSpecs.service_name}
              onSave={async () => {
                // Refresh statuses after save
                await checkWorkOrderStatuses();
              }}
            />
          )}
          
          {/* Service Agreement Modal */}
          {selectedWorkOrderForAgreement && (
            <ServiceAgreementModal
              isOpen={isAgreementModalOpen}
              onClose={() => {
                setIsAgreementModalOpen(false);
                setSelectedWorkOrderForAgreement(null);
              }}
              workOrderId={selectedWorkOrderForAgreement.id}
              workOrderName={selectedWorkOrderForAgreement.service_name}
              projectId={project?.id}
              onAgreementSigned={async () => {
                // Refresh statuses after signing
                await checkWorkOrderStatuses();
              }}
            />
          )}
          
          {/* Drag Overlay */}
          <DragOverlay>
            {activeId && activeService ? (
              <div className="p-3 rounded-lg bg-white dark:bg-gray-800 border-2 border-purple-500 shadow-xl opacity-90">
                <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100">
                  {activeService.name}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Drop into your work order
                </p>
              </div>
            ) : null}
          </DragOverlay>
        </>
      </SortableContext>
    </DndContext>
  );
};

export default ProjectServicesPage;